import { getPipelineStageIndex } from './uploadProgress';
import { FRIENDLY_STAGE_LABELS } from '../constants/pipelineStages';

/**
 * Shopify embedded app pipeline stage vocabulary:
 * UPLOADING → QUEUEING → BUILDING 3D VIEWER → DONE
 * (Semantic indexing is not surfaced in Shopify UI.)
 *
 * Product card badges use {@link SHOPIFY_OFFICIAL_STAGE_LABELS} to match the
 * reconstruction pipeline bar. Modal headlines keep the simplified Shopify labels.
 *
 * Percent labels are shown only during live browser upload (real byte progress).
 * Queue and pipeline stages use labels + segment bar only — holistic % is synthetic.
 */

export const SHOPIFY_CARD_PIPELINE_STEP_TOTAL = 4;

/** Uppercase labels from the official 4-segment reconstruction pipeline bar. */
export const SHOPIFY_OFFICIAL_STAGE_LABELS = FRIENDLY_STAGE_LABELS.map((label) => label.toUpperCase());

export const SHOPIFY_STAGE = {
  UPLOADING: 'UPLOADING',
  QUEUEING: 'QUEUEING',
  BUILDING: 'BUILDING 3D VIEWER',
  FINALIZING: 'FINALIZING',
  DONE: 'DONE',
};

/** @typedef {'uploading'|'queue'|'building'|'finalizing'|'done'|'failed'|'idle'} ShopifyStageTone */

/** Static card bar fill per tone — not tied to synthetic holistic pipeline percent. */
const CARD_BAR_PROGRESS_BY_TONE = {
  queue: 12,
  building: 38,
  finalizing: 92,
  done: 100,
  failed: 0,
  idle: 0,
};

function isLiveUploadPhase(uploadPhase) {
  return uploadPhase === 'uploading';
}

/**
 * @param {object} input
 * @param {'uploading'|'queueing'|'pipeline'|'done'|'failed'|'idle'|null} [input.uploadPhase]
 * @param {string} [input.progressMessage]
 * @param {string} [input.stageTitle]
 * @param {string|null} [input.runpodJobStatus]
 * @param {string} [input.processingStatus]
 * @param {number|null} [input.progressPercent]
 * @param {number|null} [input.stageIndex]
 * @param {boolean} [input.isFailed]
 * @param {boolean} [input.isCompleted]
 * @param {boolean} [input.isViewerReady] When false, completed/processing stays on BUILDING until SOG or small PLY is ready.
 */
export function resolveShopifyPipelineStage(input = {}) {
  const {
    uploadPhase = null,
    progressMessage = '',
    stageTitle = '',
    runpodJobStatus = null,
    processingStatus = '',
    progressPercent = null,
    stageIndex = null,
    isFailed = false,
    isCompleted = false,
    isViewerReady = null,
  } = input;

  if (isFailed) {
    return {
      label: 'FAILED',
      tone: 'failed',
      showPercent: false,
      percent: null,
    };
  }

  const viewerStillPreparing = isViewerReady === false;
  if (isCompleted || uploadPhase === 'done' || processingStatus === 'completed') {
    if (viewerStillPreparing) {
      return {
        label: SHOPIFY_STAGE.FINALIZING,
        tone: 'finalizing',
        showPercent: false,
        percent: progressPercent != null ? Math.round(progressPercent) : null,
      };
    }
    return {
      label: SHOPIFY_STAGE.DONE,
      tone: 'done',
      showPercent: false,
      percent: 100,
    };
  }

  if (isLiveUploadPhase(uploadPhase)) {
    const pct = progressPercent != null ? Math.round(progressPercent) : null;
    return {
      label: SHOPIFY_STAGE.UPLOADING,
      tone: 'uploading',
      showPercent: pct != null,
      percent: pct,
    };
  }

  const rp = String(runpodJobStatus || '').toLowerCase();
  const msg = `${progressMessage} ${stageTitle}`.toLowerCase();
  /** Stage 0 = post-upload queue; only stage ≥2 (frame extraction+) means worker has advanced. */
  const stageAdvanced = typeof stageIndex === 'number' && !Number.isNaN(stageIndex) && stageIndex >= 2;
  const hasProgress = progressPercent != null && progressPercent > 0;
  const isQueue =
    (uploadPhase === 'queueing' && !stageAdvanced && !hasProgress)
    || (processingStatus === 'queued' && !stageAdvanced && !hasProgress)
    || (rp === 'in_queue' && !stageAdvanced && !hasProgress)
    || (msg.includes('queue') && !stageAdvanced && !hasProgress);

  if (isQueue) {
    return {
      label: SHOPIFY_STAGE.QUEUEING,
      tone: 'queue',
      showPercent: false,
      percent: progressPercent != null ? Math.round(progressPercent) : null,
    };
  }

  const pct = progressPercent != null ? Math.round(progressPercent) : null;
  if (pct >= 100 && !viewerStillPreparing) {
    return {
      label: SHOPIFY_STAGE.DONE,
      tone: 'done',
      showPercent: false,
      percent: 100,
    };
  }

  return {
    label: SHOPIFY_STAGE.BUILDING,
    tone: 'building',
    showPercent: false,
    percent: pct,
  };
}

/** Headline for modal top line: "LABEL" or "UPLOADING (N%)" during live upload only. */
export function formatShopifyPipelineHeadline(stage) {
  if (!stage?.label) return '';
  if (stage.showPercent && stage.percent != null) {
    return `${stage.label} (${stage.percent}%)`;
  }
  return stage.label;
}

function officialStageLabelForInternalStage(internalStage) {
  const stage = Number(internalStage);
  if (!Number.isFinite(stage)) return SHOPIFY_OFFICIAL_STAGE_LABELS[0];
  if (stage <= 0) return SHOPIFY_STAGE.QUEUEING;
  if (stage <= 1) return SHOPIFY_OFFICIAL_STAGE_LABELS[0];
  if (stage === 2) return SHOPIFY_OFFICIAL_STAGE_LABELS[1];
  if (stage === 3) return SHOPIFY_OFFICIAL_STAGE_LABELS[2];
  return SHOPIFY_OFFICIAL_STAGE_LABELS[3];
}

function normalizeShopifyCardPipelineBarLabel(rawLabel) {
  const upper = String(rawLabel || '').trim().toUpperCase();
  if (!upper) return '';
  if (upper.includes('QUEUE')) return SHOPIFY_STAGE.QUEUEING;
  if (upper.includes('UPLOAD')) return SHOPIFY_OFFICIAL_STAGE_LABELS[0];
  if (upper.includes('FRAME EXTRACTION') || upper.includes('IMAGE EXTRACTION')) {
    return SHOPIFY_OFFICIAL_STAGE_LABELS[1];
  }
  if (upper.includes('STRUCTURE FROM MOTION') || upper.includes('SFM') || upper.includes('ANALYZING')) {
    return SHOPIFY_OFFICIAL_STAGE_LABELS[2];
  }
  if (
    upper.includes('GAUSSIAN')
    || upper.includes('CREATING 3D')
    || upper.includes('BUILDING 3D')
    || upper.includes('FINALIZING')
  ) {
    return SHOPIFY_OFFICIAL_STAGE_LABELS[3];
  }
  if (upper.includes('COMPLETE') || upper === 'DONE') return SHOPIFY_STAGE.DONE;
  return upper;
}

/**
 * Card badge label from modal/upload progress — matches official pipeline bar phrasing.
 */
export function resolveShopifyCardPipelineLabelFromProgress({
  shopifyHeadlineStage,
  pipelineStageLabel: polledBarLabel,
  internalStageIndex,
} = {}) {
  const headline = shopifyHeadlineStage || {};
  const tone = headline.tone;

  if (tone === 'uploading') return SHOPIFY_OFFICIAL_STAGE_LABELS[0];
  if (tone === 'finalizing') return SHOPIFY_OFFICIAL_STAGE_LABELS[3];
  if (tone === 'done') return SHOPIFY_STAGE.DONE;

  if (internalStageIndex != null && Number.isFinite(Number(internalStageIndex))) {
    const official = officialStageLabelForInternalStage(Number(internalStageIndex));
    if (Number(internalStageIndex) >= 2 || tone !== 'queue') return official;
  }

  if (tone === 'queue') return SHOPIFY_STAGE.QUEUEING;

  if (polledBarLabel) {
    return normalizeShopifyCardPipelineBarLabel(polledBarLabel);
  }

  return SHOPIFY_OFFICIAL_STAGE_LABELS[3];
}

/** Normalize any card stage label to official pipeline bar phrasing. */
export function formatShopifyCardStageLabel(product = {}, cardStage = null) {
  if (!cardStage?.label) return '';

  const tone = product.pipelineStageTone || cardStage.tone;
  if (tone === 'uploading') return SHOPIFY_OFFICIAL_STAGE_LABELS[0];
  if (tone === 'finalizing') return SHOPIFY_OFFICIAL_STAGE_LABELS[3];
  if (tone === 'done') return SHOPIFY_STAGE.DONE;

  const internal = product.internalPipelineStage;
  if (internal != null && Number.isFinite(Number(internal))) {
    const official = officialStageLabelForInternalStage(Number(internal));
    if (Number(internal) >= 2 || tone !== 'queue') return official;
  }

  if (tone === 'queue') return SHOPIFY_STAGE.QUEUEING;

  return normalizeShopifyCardPipelineBarLabel(cardStage.label);
}

/** 1-based step for card badge (e.g. 3/4 during SfM). */
export function resolveShopifyCardPipelineStep(product = {}, cardStage = null) {
  const tone = product.pipelineStageTone || cardStage?.tone;
  const label = String(product.pipelineStageLabel || cardStage?.label || '').trim().toUpperCase();

  const internal = product.internalPipelineStage;
  if (internal != null && Number.isFinite(Number(internal))) {
    const stage = Number(internal);
    if (stage <= 1) return 1;
    if (stage === 2) return 2;
    if (stage === 3) return 3;
    return SHOPIFY_CARD_PIPELINE_STEP_TOTAL;
  }

  if (/\bUPLOAD/.test(label) || /\bQUEUE/.test(label) || /VIDEO UPLOAD/.test(label)) return 1;
  if (/IMAGE EXTRACTION|FRAME EXTRACTION/.test(label)) return 2;
  if (/ANALYZING|STRUCTURE FROM MOTION|\bSFM\b/.test(label)) return 3;
  if (/FINALIZING|BUILDING 3D|CREATING 3D|GAUSSIAN/.test(label)) return SHOPIFY_CARD_PIPELINE_STEP_TOTAL;
  if (tone === 'uploading' || tone === 'queue') return 1;
  if (tone === 'finalizing' || tone === 'done' || tone === 'building') return SHOPIFY_CARD_PIPELINE_STEP_TOTAL;

  const barIndex = getPipelineStageIndex(product.stageIndex ?? cardStage?.stageIndex);
  if (barIndex != null && Number.isFinite(Number(barIndex))) {
    return Math.min(SHOPIFY_CARD_PIPELINE_STEP_TOTAL, Math.max(1, Number(barIndex) + 1));
  }

  return 1;
}

/** Product card stage from catalogue row or active-upload overlay. */
export function resolveShopifyCardStage(product = {}) {
  const {
    state,
    progressPercent,
    pipelineStageLabel,
    pipelineStageTone,
    uploadPhase,
    runpodJobStatus,
  } = product;

  if (state === 'failed') return null;

  if (state === 'processing' && pipelineStageLabel && pipelineStageTone) {
    const showPercent = pipelineStageTone === 'uploading' && progressPercent != null;
    return {
      label: pipelineStageLabel,
      tone: pipelineStageTone,
      showPercent,
      percent: progressPercent != null ? Math.round(progressPercent) : null,
    };
  }

  if (state !== 'processing') return null;

  return resolveShopifyPipelineStage({
    uploadPhase,
    runpodJobStatus,
    progressPercent,
    processingStatus: 'processing',
  });
}

/** Card progress bar value — real upload % only; otherwise a static tone-based fill. */
export function resolveShopifyCardBarProgress(stage, fallbackPercent = 0) {
  if (!stage) return 0;
  if (stage.tone === 'uploading' && stage.percent != null) {
    return Math.max(4, Math.min(100, stage.percent));
  }
  if (stage.tone === 'uploading' && Number.isFinite(Number(fallbackPercent))) {
    return Math.max(4, Math.min(100, Number(fallbackPercent)));
  }
  return CARD_BAR_PROGRESS_BY_TONE[stage.tone] ?? 8;
}
